import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { BeatLoader } from 'react-spinners';
import '../../../../../styles/count_graphs.css';
import { url } from '../../../../../api/endpointURL';
import LprMonthlyCountChart from './LprMonthlyCountGraph';
import LprWeeklyCountChart from './LprWeeklyCountGraph';
import LprDailyCountChart from './LprDailyCountGraph';
import { 
    filterMonthlyDataByCamera,
    filterWeeklyDataByCamera,
    filterDailyDataByCamera,
    getPercentageIncrease,
    getTotalForPeriod,
    checkForEmptyData
} from '../baseLprUtils';

const LprCountAnalytics = ({
    selectedSite,
    selectedCamera, 
    selectedCameraId, 
    selectedYear,
    handleCountDataState

}) => {
    const [monthlyLprCount, setMonthlyLprCount] = useState([]);
    const [monthlyLprLoading, setMonthlyLprLoading] = useState(false);

    const [weeklyLprCount, setWeeklyLprCount] = useState([]);
    const [weeklyLprLoading, setWeeklyLprLoading] = useState(false);
    
    const [dailyLprCount, setDailyLprCount] = useState([]);
    const [dailyLprLoading, setDailyLprLoading] = useState(false);

    const [selectedMonth, setSelectedMonth] = useState();
    const [selectedWeek, setSelectedWeek] = useState();
    const [selectedWeekStartDate, setSelectedWeekStarDate] = useState();
    const [selectedRelativeWeek, setSelectedRelativeWeek] = useState();
    const [selectedDay, setSelectedDay] = useState();
    const [selectedDayOfWeek, setSelectedDayOfWeek] = useState();

    const [totalVehicles, setTotalVehicle] = useState();

    const monthlyAbortControllerRef = useRef(null);
    const weeklyAbortControllerRef = useRef(null);
    const dailyAbortControllerRef = useRef(null);

    const aggregatedMonthlyData = useMemo(() => 
        filterMonthlyDataByCamera(monthlyLprCount, selectedCameraId, 'total_count'),
        [monthlyLprCount, selectedCameraId]
    );

    const aggregatedWeeklyData = useMemo(() => 
        filterWeeklyDataByCamera(weeklyLprCount, selectedCameraId, 'total_count'),
        [weeklyLprCount, selectedCameraId]
    );

    const aggregatedDailyData = useMemo(() => 
        filterDailyDataByCamera(dailyLprCount, selectedCameraId, 'total_count'),
        [dailyLprCount, selectedCameraId]
    );

    // Hiding empty graphs
    const [initialFetchCompleted, setInitialFetchCompleted] = useState(false);
    const [showCount, setShowCount] = useState(true);

    const prevCameraRef = useRef(selectedCameraId);
    
    useEffect(() => {
        const cameraChanged = prevCameraRef.current !== selectedCameraId;
        prevCameraRef.current = selectedCameraId;

        if (cameraChanged) {
            setInitialFetchCompleted(false);
        }
    }, [selectedCameraId]);

    const hasActualData = useCallback(() => {
        const hasData = checkForEmptyData(aggregatedMonthlyData);
        if (!hasData) {
            return false;
        } else {
            return true;
        }


    }, [aggregatedMonthlyData]);


    const fetchMonthlyLprCount = useCallback(async () => {
        if (monthlyAbortControllerRef.current) {
            monthlyAbortControllerRef.current.abort();
        }

        monthlyAbortControllerRef.current = new AbortController();
        const { signal } = monthlyAbortControllerRef.current;
        try {
            setMonthlyLprLoading(true);

            const response = await fetch(`${url}/lprCount/monthlyLprCount?year=${selectedYear}&hwid=${selectedSite?.hwid}`, { signal });
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }
            const responseData = await response.json();

            // console.log("monthly lpr count data ========= ", responseData);

            setMonthlyLprLoading(false);

            setMonthlyLprCount(responseData);

            setInitialFetchCompleted(true);

            if (responseData.length) {
                // console.log("found response data from monthly lpr count fetch. Data === ", responseData);
                const latestMonthWithData = [...responseData]
                    .reverse()
                    .find(month => month.total_count > 0);

                if (latestMonthWithData) {
                    // console.log("found latest month with data.");
                    // console.log("setting the selected month to: ", latestMonthWithData.month);
                    setSelectedMonth(latestMonthWithData.month);
                }
            }

            // console.log("selected month after being set post monthly fetch is: ", selectedMonth);


        } catch (e) {
            if (e.name === 'AbortError') {
                // console.log("Fetch monthly lpr count data aborted");
            }
            // console.log(e)
        } finally {
            setMonthlyLprLoading(false);
        }
    }, [selectedYear, selectedSite.hwid]);


    const fetchWeeklyLprCount = useCallback(async () => {
        if (weeklyAbortControllerRef.current) {
            weeklyAbortControllerRef.current.abort();
        }

        weeklyAbortControllerRef.current = new AbortController();
        const { signal } = weeklyAbortControllerRef.current;
        try {
            // console.log("fetching weekly lpr count data");

            setWeeklyLprLoading(true);

            const response = await fetch(`${url}/lprCount/weeklyLprCount?year=${selectedYear}&month=${selectedMonth}&hwid=${selectedSite?.hwid}`, { signal });
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }
            const responseData = await response.json();

            // console.log("weekly lpr count data ========= ", responseData);

            setWeeklyLprLoading(false);

            setWeeklyLprCount(responseData);

            if (responseData.length) {
                const latestWeekWithData = [...responseData]
                    .reverse()
                    .find(week => week.total_count > 0);

                if (latestWeekWithData) {
                    // console.log("found latest week with data.");
                    // console.log("setting the selected week to: ", latestWeekWithData.week);
                    setSelectedWeek(latestWeekWithData.week);
                    setSelectedWeekStarDate(latestWeekWithData.week_start_date);
                    setSelectedRelativeWeek(latestWeekWithData.relative_week_number);
                }

            }

        } catch (e) {
            if (e.name === 'AbortError') {
                // console.log("fetching weekly lpr count data aborted");
            }
            // console.log(e)
        } finally {
            setWeeklyLprLoading(false);
        }
    }, [selectedYear, selectedMonth, selectedSite.hwid]);

    const fetchDailyLprCount = useCallback(async () => {
        if (dailyAbortControllerRef.current) {
            dailyAbortControllerRef.current.abort();
        }

        dailyAbortControllerRef.current = new AbortController();
        const { signal } = dailyAbortControllerRef.current;
        try {
            // console.log("fetching daily lpr count data");
            // console.log("The selected week prior to fetching lpr count data::: ", selectedWeek);

            setDailyLprLoading(true);

            const response = await fetch(`${url}/lprCount/dailyLprCount?year=${selectedYear}&month=${selectedMonth}&week=${selectedWeek}&hwid=${selectedSite?.hwid}`, {signal});
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }
            const responseData = await response.json();

            // console.log("daily lpr count data ========= ", responseData);

            setDailyLprLoading(false);

            setDailyLprCount(responseData);

            if (responseData.length) {
                const latestDayWithData = [...responseData]
                    .reverse()
                    .find(day => day.total_count > 0);

                if (latestDayWithData) {
                    // console.log("found latest day with data.");
                    // console.log("setting the selected day to: ", latestDayWithData.day_number);
                    setSelectedDay(latestDayWithData.day_number);
                    setSelectedDayOfWeek(latestDayWithData.day);
                }
            }

        } catch (e) {
            if (e.name === "AbortError") {
                // console.log("daily lpr count data fetch aborted.");
            } 
            // console.log(e)
        } finally {
            setDailyLprLoading(false);
        }
    }, [selectedYear, selectedMonth, selectedWeek, selectedSite?.hwid]);

    const calculateTotalVehicles = (monthlyData) => {
        let total = 0;

        monthlyData.forEach(month => {
            const count = parseInt(month.total_count);
            const safeCount = isNaN(count) ? 0 : count;
            // console.log("This month's count is:: ", safeCount);
            total += safeCount;
        });

        // console.log("total vehicles after adding month values together: ", total);

        return total;
    }

    useEffect(() => {
        return () => {
            if (monthlyAbortControllerRef.current) monthlyAbortControllerRef.current.abort();
            if (weeklyAbortControllerRef.current) weeklyAbortControllerRef.current.abort();
            if (dailyAbortControllerRef.current) dailyAbortControllerRef.current.abort();
        };
    }, []);

    useEffect(() => {
        if (initialFetchCompleted) {
            const hasData = hasActualData();
            setShowCount(hasData);

            if (handleCountDataState) {
                handleCountDataState(hasData);
            }
        }
    }, [initialFetchCompleted, hasActualData, handleCountDataState]);

    useEffect(() => {
        // console.log("hwid in the useEffect prior to fetching the monthly lpr count data", selectedSite?.hwid);
        if (selectedYear && selectedSite?.hwid) {
            fetchMonthlyLprCount();
        }
    }, [selectedYear, selectedSite?.hwid, fetchMonthlyLprCount]);

    useEffect(() => {
        if (monthlyLprCount.length > 0) {
            let total = calculateTotalVehicles(monthlyLprCount);
            // console.log("total vehicles in the use effect is: ", total);
            setTotalVehicle(total);
        }
    }, [monthlyLprCount]);

    useEffect(() => {
        if (selectedMonth > 0) {
            // console.log("monthly data for this year exists. Fetching Weekly data.");
            // console.log("the selected month in the useEffect prior to fetching weekly data is: ", selectedMonth);
            fetchWeeklyLprCount();
        }

    }, [selectedYear, selectedMonth, selectedSite?.hwid, monthlyLprCount, fetchWeeklyLprCount]);

    useEffect(() => {
        // console.log("The selected week in the useEffect prior to fetching daily lpr count data is::", selectedWeek);
        if (selectedWeek) {
            fetchDailyLprCount();
        }
    }, [selectedYear, selectedMonth, selectedWeek, selectedSite?.hwid, weeklyLprCount, fetchDailyLprCount]);

    return (
        <>
            {
                true ? (
                    <>
                        {/* <div className={'totals-container'}>
                            <div className='insights-container'>
                                <span className='insights-label'> Total Vehicles Detected this year</span> <br />
                                {monthlyLprLoading ? <BeatLoader color={'white'} size={10} /> : <span className='insights-value'>{totalVehicles}</span>}
                            </div> */}

                        {/* </div> */}
                        <div className='graphs-container'>
                            <div className='monthly-bar-chart-container'>
                                <LprMonthlyCountChart
                                    selectedYear={selectedYear}
                                    monthlyLprCountData={aggregatedMonthlyData}
                                    monthlyDataLoading={monthlyLprLoading}
                                    selectedMonth={selectedMonth}
                                    handleMonthlyBarClick={(month) => {
                                        setSelectedMonth(month);
                                        fetchWeeklyLprCount();
                                    }}
                                    monthlyVehiclePercentageIncrease={getPercentageIncrease(aggregatedMonthlyData, selectedMonth, 'month', 'total_count')}
                                    monthlyVehicles={getTotalForPeriod(aggregatedMonthlyData, selectedMonth, 'month', 'total_count')}
                                />
                            </div>
                            <div className='weekly-bar-chart-container'>
                                <LprWeeklyCountChart
                                    selectedYear={selectedYear}
                                    weeklyLprCountData={aggregatedWeeklyData}
                                    weeklyDataLoading={weeklyLprLoading}
                                    selectedMonth={selectedMonth}
                                    selectedWeek={selectedWeek}
                                    relativeWeekNumber={selectedRelativeWeek}
                                    onBarClick={(week) => {
                                        setSelectedWeek(week);
                                        fetchDailyLprCount();
                                    }}
                                    weeklyVehiclePercentageIncrease={getPercentageIncrease(aggregatedWeeklyData, selectedWeek, 'week', 'total_count')}
                                    weeklyVehicles={getTotalForPeriod(aggregatedWeeklyData, selectedWeek, 'week', 'total_count')}
                                />
                            </div>
                            <div className='daily-bar-chart-container'>
                                <LprDailyCountChart
                                    selectedYear={selectedYear}
                                    dailyLprCountData={aggregatedDailyData}
                                    dailyDataLoading={dailyLprLoading}
                                    selectedMonth={selectedMonth}
                                    selectedWeek={selectedWeek}
                                    selectedWeekStartDate={selectedWeekStartDate}
                                    relativeWeekNumber={selectedRelativeWeek}
                                    selectedDay={selectedDay}
                                    selectedDayOfWeek={selectedDayOfWeek}
                                    dailyVehiclePercentageIncrease={getPercentageIncrease(aggregatedDailyData, selectedDay, 'day_number', 'total_count')}
                                    dailyVehicles={getTotalForPeriod(aggregatedDailyData, selectedDay, 'day_number', 'total_count')}
                                />
                            </div>
                        </div>

                    </>
                )
                    :
                    <div style={{ display: 'none' }} />
            }
        </>
    );
}

export default LprCountAnalytics;